<script setup lang="ts">
/**
 * AppLayout - Main Application Layout
 *
 * Global layout with header (project) and vertical navigation
 */

import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useRouter, useRoute, RouterView } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  Menu,
  Plus,
  Clock,
  Coin,
  Document,
  Grid,
  DataAnalysis,
  Connection,
  Operation,
  User,
  UserFilled,
  Lock,
  SwitchButton,
  CloseBold,
} from "@element-plus/icons-vue";
import {
  ElAside,
  ElAvatar,
  ElButton,
  ElContainer,
  ElDialog,
  ElDivider,
  ElForm,
  ElFormItem,
  ElHeader,
  ElIcon,
  ElInput,
  ElMain,
  ElMessage,
  ElOption,
  ElPopover,
  ElSelect,
  ElTag,
  ElTooltip,
} from "element-plus";
import type { ICreateProjectRequest } from "@/shared/api";
import { createFeatureId } from "@/shared/types";
import MbLanguageSwitcher from "@/shared/ui/atoms/MbLanguageSwitcher.vue";
import { MbUndoRedoButtons, useUndoRedoStore } from "@/features/undo-redo";
import {
  MbFeatureFormDialog,
  MbFeatureManagerDialog,
} from "@/features/feature-management";
import { useApiErrorMessage } from "@/shared/lib";
import { useAuthStore } from "@/entities/auth";
import { useProjectsStore } from "@/entities/projects";
import { useFeaturesStore } from "@/entities/features";
import { useBmVersioningStore } from "@/features/bm-versioning";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
/**
 * Показ отказа по машиночитаемому признаку причины (api-errors-R08): занятое
 * имя проекта, пустое имя и отказ в доступе различимы между собой, потому что
 * различимы их признаки. Действие, которое не выполнено, не остаётся
 * незамеченным (R09).
 */
const { showApiError } = useApiErrorMessage();

/**
 * Служебное значение опции «создать…» в селекторах шапки.
 *
 * В состояние выбора оно не попадает никогда: обработчик по нему только
 * открывает диалог (projects-R08, feature-lifecycle-R26).
 */
const CREATE_OPTION_VALUE = "__create_new__";

/**
 * Предел длины сообщения коммита — тот же, что у продукта
 * (business-model-versioning-R05): счётчик и ограничение ввода считают
 * символы, а не байты, поэтому кириллица и латиница упираются в предел
 * одинаково.
 */
const MAX_COMMIT_MESSAGE_LENGTH = 1000;

const authStore = useAuthStore();
const projectsStore = useProjectsStore();
const featuresStore = useFeaturesStore();
const versioningStore = useBmVersioningStore();
const undoRedoStore = useUndoRedoStore();

// Dialogs (UI-only state)
const showCreateProjectDialog = ref(false);
const showCreateFeatureDialog = ref(false);
/** Перечень фич проекта — поверхность управления фичами (feature-lifecycle-R11). */
const showFeatureManagerDialog = ref(false);
const showCommitDialog = ref(false);
const showHistoryDialog = ref(false);
const showCheckoutDialog = ref(false);
const checkoutTargetFeatureId = ref<string>("");
const checkoutBranchName = ref("");
const commitMessage = ref("");
const newProjectForm = ref<ICreateProjectRequest>({
  name: "",
  description: "",
});

// Navigation items (computed to be reactive to language changes)
const navItems = computed(() => [
  {
    id: "source-explorer",
    icon: Coin,
    label: t("nav.sourceExplorer"),
    path: "/",
  },
  {
    id: "bm",
    icon: Document,
    label: t("nav.businessLayer"),
    path: "/business-model",
    disabled: false,
  },
  {
    id: "mapping",
    icon: Grid,
    label: t("nav.mapping"),
    path: "/mapping",
    disabled: false,
  },
  {
    id: "datamarts",
    icon: DataAnalysis,
    label: t("nav.datamarts"),
    path: "/datamarts",
    disabled: false,
  },
  {
    id: "change-history",
    icon: Clock,
    label: t("nav.changeHistory"),
    path: "/change-history",
    disabled: false,
  },
  {
    id: "etl",
    icon: Connection,
    label: t("nav.etl"),
    path: "/etl",
    disabled: true,
  },
]);

/**
 * Разделы, работающие в контексте выбранной фичи: только в них показывается
 * выбор фичи. Раздел витрин входит сюда наравне с бизнес-моделью и маппингом
 * (норма: datamarts-datamarts-R01 — выбор фичи доступен прямо в разделе витрин).
 */
const FEATURE_CONTEXT_ROUTES: readonly string[] = [
  "/business-model",
  "/mapping",
  "/datamarts",
];

const isFeatureContextRoute = computed<boolean>(() =>
  FEATURE_CONTEXT_ROUTES.includes(route.path)
);

// Watch project selection to load features and init edit session.
// Служебное значение сюда дойти не может (см. CREATE_OPTION_VALUE), поэтому
// зависимый контекст перезагружается только на действительной смене проекта.
watch(() => projectsStore.selectedProjectId, async (newProjectId, previousProjectId) => {
  // Сеанс редактирования принадлежит одному проекту: покидая проект, клиент
  // закрывает свой сеанс, и лишь затем открывает новый в выбранном
  // (changelog-undo-redo/edit-sessions.md R03, R04; administration/projects.md
  // R09). Новый сеанс начинает с пустыми стеками — накопленное прежним ему не
  // переходит (R13), но из аудит-лога не исчезает: оно отменяется из истории
  // изменений своего проекта.
  if (previousProjectId.length > 0) {
    await undoRedoStore.destroy();
  }

  if (newProjectId.length > 0) {
    await featuresStore.loadFeatures(newProjectId);
    // Initialize undo/redo edit session for this project
    await undoRedoStore.init(Number(newProjectId));
    // Перечень нового проекта пересчитывает выбор фичи (features-R10/R11):
    // фича прежнего проекта выбранной не остаётся, поэтому сюда приходит
    // либо фича текущего проекта, либо пусто — отмена доступна по R24.
    undoRedoStore.setFeatureSelected(
      featuresStore.selectedFeatureId.length > 0,
      isFeatureOfCurrentProject(featuresStore.selectedFeatureId),
    );
  } else {
    featuresStore.clearFeatures();
    await undoRedoStore.destroy();
  }
});

/**
 * Принадлежит ли выбранная фича текущему проекту.
 *
 * Перечень фич загружается по проекту, поэтому «фича есть в перечне» и значит
 * «фича этого проекта». Отмена и возврат на странице чужой фичи не
 * предлагаются (undo-redo-R24).
 */
function isFeatureOfCurrentProject(featureId: string): boolean {
  if (featureId.length === 0) return true;
  return featuresStore.features.some((feature) => feature.id === featureId);
}

// Sync feature selection with versioning store
watch(() => featuresStore.selectedFeatureId, async (newFeatureId) => {
  // Отмена и возврат доступны только при выбранной фиче
  // (business-model-versioning-R21, основание: вердикт 232): подстановки
  // фичи по умолчанию нет ни на одной поверхности.
  undoRedoStore.setFeatureSelected(
    newFeatureId.length > 0,
    isFeatureOfCurrentProject(newFeatureId),
  );

  if (newFeatureId.length > 0) {
    versioningStore.setCurrentFeature(createFeatureId(newFeatureId));
    await versioningStore.loadHistory();
  } else {
    versioningStore.setCurrentFeature(null);
  }
}, { immediate: true });

// Load projects on mount
onMounted(async () => {
  await projectsStore.loadProjects();
});

// Cleanup edit session on unmount
onBeforeUnmount(() => {
  void undoRedoStore.destroy();
});

/**
 * Выбор в селекторе проекта.
 *
 * Норма: spec/features/administration/projects.md, R08 (основание: вердикт
 * 270) — открытие диалога создания текущий выбор **не меняет**: ни проект, ни
 * зависимый от него контекст (перечень и выбор фичи, сеанс редактирования) не
 * перезагружаются, пока пользователь не завершил диалог. Поэтому служебное
 * значение в состояние выбора не попадает вовсе: оно лишь открывает диалог.
 * Выбранным проект становится только при успешном создании (R08, вторая
 * половина — в редьюсере `CREATE_PROJECT_SUCCESS`).
 */
function handleProjectChange(projectId: string): void {
  if (projectId === CREATE_OPTION_VALUE) {
    openCreateProjectDialog();
    return;
  }
  projectsStore.selectProject(projectId);
}

/**
 * Выбор в селекторе фичи — то же правило.
 *
 * Норма: spec/features/business-model/feature-lifecycle.md, R26 (основание:
 * вердикт 274, распространение вердикта 270 на второй селектор шапки):
 * открытие диалога создания фичи выбор и зависимый контекст (дерево модели,
 * диаграммы, маппинги, история версий) не меняет. Оба селектора шапки ведут
 * себя одинаково — открытие диалога есть заявление намерения, а не смена
 * рабочего контекста.
 */
function handleFeatureChange(featureId: string): void {
  if (featureId === CREATE_OPTION_VALUE) {
    openCreateFeatureDialog();
    return;
  }
  featuresStore.selectFeature(featureId);
}

// Project dialogs
function openCreateProjectDialog(): void {
  showCreateProjectDialog.value = true;
}

function closeCreateProjectDialog(): void {
  showCreateProjectDialog.value = false;
  newProjectForm.value = { name: "", description: "" };
}

async function handleCreateProject(): Promise<void> {
  await projectsStore.createProject(newProjectForm.value);
  if (projectsStore.error !== null) {
    showApiError(projectsStore.error);
    return;
  }
  closeCreateProjectDialog();
}

// Feature dialogs
function openCreateFeatureDialog(): void {
  if (projectsStore.selectedProjectId.length === 0) {
    ElMessage.warning({ message: t("feature.selectProjectFirst") });
    return;
  }
  showCreateFeatureDialog.value = true;
}

/**
 * Перечень фич проекта: заведение, свойства и удаление фичи живут там
 * (feature-lifecycle-R01, R04, R11). Сам селектор действия удаления не
 * содержит — он выбирает фичу и предлагает её создание (features-R11).
 */
function openFeatureManagerDialog(): void {
  showFeatureManagerDialog.value = true;
}

// Commit handlers
function openCommitDialog(): void {
  commitMessage.value = "";
  showCommitDialog.value = true;
}

function closeCommitDialog(): void {
  showCommitDialog.value = false;
  commitMessage.value = "";
}

async function handleCommit(): Promise<void> {
  const newFeatureId = await versioningStore.commit(
    commitMessage.value || undefined
  );
  if (newFeatureId) {
    // Update selected feature to new HEAD
    featuresStore.selectFeature(String(newFeatureId));
    ElMessage.success({ message: t("versioning.commitSuccess") });
    closeCommitDialog();
    // Reload features list
    await featuresStore.loadFeatures(projectsStore.selectedProjectId);
  } else {
    ElMessage.error({ message: t("versioning.commitError") });
  }
}

// History handlers
function openHistoryDialog(): void {
  showHistoryDialog.value = true;
}

function closeHistoryDialog(): void {
  showHistoryDialog.value = false;
}

function handleCheckout(commitFeatureId: string): void {
  checkoutTargetFeatureId.value = commitFeatureId;
  checkoutBranchName.value = "";
  showCheckoutDialog.value = true;
}

function closeCheckoutDialog(): void {
  showCheckoutDialog.value = false;
  checkoutTargetFeatureId.value = "";
  checkoutBranchName.value = "";
}

async function confirmCheckout(): Promise<void> {
  const branchName = checkoutBranchName.value.trim() || undefined;
  const newFeatureId = await versioningStore.checkout(
    createFeatureId(checkoutTargetFeatureId.value),
    branchName
  );
  if (newFeatureId) {
    // Update selected feature to new branch
    featuresStore.selectFeature(String(newFeatureId));
    ElMessage.success({ message: t("versioning.checkoutSuccess") });
    closeCheckoutDialog();
    closeHistoryDialog();
    // Reload features list
    await featuresStore.loadFeatures(projectsStore.selectedProjectId);
  } else {
    ElMessage.error({ message: t("versioning.checkoutError") });
  }
}

// Auth handlers
async function handleLogout(): Promise<void> {
  try {
    await authStore.logout();
    void router.push("/login");
  } catch (error) {
    console.error("Logout error:", error);
  }
}

async function handleLogoutAll(): Promise<void> {
  try {
    await authStore.logoutAll();
    void router.push("/login");
  } catch (error) {
    console.error("Logout all error:", error);
  }
}

// Provide context to child components
defineExpose({
  selectedProjectId: computed(() => projectsStore.selectedProjectId),
  selectedFeatureId: computed(() => featuresStore.selectedFeatureId),
});
</script>

<template>
  <ElContainer class="app-layout">
    <!-- Global Header -->
    <ElHeader class="app-header">
      <div class="header-left">
        <ElButton :icon="Menu" circle size="default" :title="t('app.menu')" />
        <span class="app-title">{{ t("app.title") }}</span>

        <!-- Project Selector -->
        <ElSelect
          :model-value="projectsStore.selectedProjectId"
          :placeholder="t('project.select')"
          size="default"
          class="header-selector"
          @update:model-value="handleProjectChange"
        >
          <ElOption
            v-for="project in projectsStore.projects"
            :key="project.id"
            :label="project.name"
            :value="project.id"
          />
          <ElDivider class="selector-divider" />
          <ElOption :value="CREATE_OPTION_VALUE">
            <ElIcon><Plus /></ElIcon>
            <span class="option-text">{{ t("project.create") }}</span>
          </ElOption>
        </ElSelect>

        <!--
          Выбор фичи. Разделы, работающие в контексте фичи: бизнес-модель,
          маппинг, витрины. Витрины — норма datamarts-datamarts-R01:
          выбор фичи доступен прямо в разделе витрин.
        -->
        <ElSelect
          v-if="projectsStore.selectedProjectId && isFeatureContextRoute"
          :model-value="featuresStore.selectedFeatureId"
          :placeholder="t('feature.select')"
          size="default"
          class="header-selector"
          @update:model-value="handleFeatureChange"
        >
          <!--
            Опции: фичи перечня плюс архивная выбранная — она встречается в
            селекторе единственным образом, как выбранная на момент архивации
            (features-R08, R11). Пометка статуса: `merged` — без пометки,
            `archived` — нейтральная, остальные — предупреждающая (R11).
          -->
          <ElOption
            v-for="feature in featuresStore.selectorOptions"
            :key="feature.id"
            :label="feature.name"
            :value="feature.id"
          >
            <span>{{ feature.name }}</span>
            <ElTag
              v-if="feature.status !== 'merged'"
              size="small"
              :type="feature.status === 'archived' ? 'info' : 'warning'"
              class="feature-status-tag"
            >
              {{ t(`feature.statuses.${feature.status}`) }}
            </ElTag>
          </ElOption>
          <ElDivider class="selector-divider" />
          <ElOption :value="CREATE_OPTION_VALUE">
            <ElIcon><Plus /></ElIcon>
            <span class="option-text">{{ t("feature.create") }}</span>
          </ElOption>
        </ElSelect>

        <!--
          Вход в перечень фич проекта: заведение, свойства и удаление фичи
          (feature-lifecycle-R01, R04, R11).
        -->
        <ElTooltip :content="t('feature.manageTitle')" placement="bottom">
          <ElButton
            v-if="projectsStore.selectedProjectId && isFeatureContextRoute"
            size="default"
            :aria-label="t('feature.manageTitle')"
            @click="openFeatureManagerDialog"
          >
            <ElIcon><Operation /></ElIcon>
          </ElButton>
        </ElTooltip>

        <!-- Versioning Controls -->
        <ElButton
          v-if="projectsStore.selectedProjectId && route.path === '/business-model'"
          type="primary"
          size="default"
          :disabled="versioningStore.isCommitting"
          :loading="versioningStore.isCommitting"
          @click="openCommitDialog"
        >
          {{ t("versioning.commit") }}
        </ElButton>

        <ElButton
          v-if="projectsStore.selectedProjectId && route.path === '/business-model'"
          size="default"
          :disabled="versioningStore.isLoadingHistory"
          @click="openHistoryDialog"
        >
          {{ t("versioning.history") }}
          <ElTag
            v-if="versioningStore.commitCount > 0"
            size="small"
            type="info"
            class="history-count-tag"
          >
            {{ versioningStore.commitCount }}
          </ElTag>
        </ElButton>
      </div>

      <div class="header-right">
        <!-- Undo/Redo Buttons -->
        <MbUndoRedoButtons />
        <!-- Language Selector -->
        <MbLanguageSwitcher />
      </div>
    </ElHeader>

    <ElContainer class="app-content">
      <!-- Vertical Navigation Sidebar -->
      <ElAside width="60px" class="nav-sidebar">
        <!-- Top navigation items -->
        <div class="nav-items-top">
          <ElTooltip
            v-for="item in navItems"
            :key="item.id"
            :content="item.label"
            placement="right"
          >
            <div
              class="nav-item"
              :class="{
                active: route.path === item.path,
                disabled: item.disabled,
              }"
              @click="!item.disabled && router.push(item.path)"
            >
              <ElIcon size="24">
                <component :is="item.icon" />
              </ElIcon>
            </div>
          </ElTooltip>
        </div>

        <!-- User button at bottom -->
        <div class="nav-items-bottom">
          <!-- Admin navigation -->
          <div v-if="authStore.isAdmin" class="nav-admin-section">
            <ElTooltip :content="t('nav.admin.users')" placement="right">
              <div
                class="nav-item"
                :class="{ active: route.path === '/admin/users' }"
                @click="router.push('/admin/users')"
              >
                <ElIcon size="24"><UserFilled /></ElIcon>
              </div>
            </ElTooltip>
          </div>

          <ElPopover placement="right" :width="220" trigger="click">
            <template #reference>
              <div class="nav-item user-button">
                <ElAvatar :size="32">
                  <ElIcon><User /></ElIcon>
                </ElAvatar>
              </div>
            </template>

            <!-- User menu content -->
            <div class="user-menu">
              <div class="user-info">
                <div class="user-login">@{{ authStore.currentUserLogin }}</div>
                <div class="user-name">
                  {{ authStore.currentUser?.fullName }}
                </div>
                <ElTag
                  v-if="authStore.isAdmin"
                  type="danger"
                  size="small"
                  class="admin-tag"
                >
                  {{ t("common.admin") }}
                </ElTag>
              </div>

              <ElDivider class="user-menu-divider" />

              <div class="user-menu-items">
                <div class="menu-item" @click="router.push('/change-password')">
                  <ElIcon><Lock /></ElIcon>
                  <span>{{ t("auth.changePassword") }}</span>
                </div>
                <div class="menu-item danger" @click="handleLogout">
                  <ElIcon><SwitchButton /></ElIcon>
                  <span>{{ t("auth.logout") }}</span>
                </div>
                <div class="menu-item danger" @click="handleLogoutAll">
                  <ElIcon><CloseBold /></ElIcon>
                  <span>{{ t("auth.logoutAll") }}</span>
                </div>
              </div>
            </div>
          </ElPopover>
        </div>
      </ElAside>

      <!-- Main Content Area -->
      <ElMain class="app-main">
        <RouterView v-slot="{ Component }">
          <component
            :is="Component"
            :project-id="projectsStore.selectedProjectId"
            :feature-id="featuresStore.selectedFeatureId"
          />
        </RouterView>
      </ElMain>
    </ElContainer>

    <!-- Create Project Dialog -->
    <ElDialog
      v-model="showCreateProjectDialog"
      :title="t('project.createTitle')"
      width="500px"
      @close="closeCreateProjectDialog"
    >
      <ElForm
        :model="newProjectForm"
        :label-width="120"
        @submit.prevent="handleCreateProject"
      >
        <ElFormItem :label="t('project.name')" required>
          <ElInput
            v-model="newProjectForm.name"
            :placeholder="t('project.namePlaceholder')"
          />
        </ElFormItem>
        <ElFormItem :label="t('project.description')">
          <ElInput
            v-model="newProjectForm.description"
            type="textarea"
            :rows="3"
            :placeholder="t('project.descriptionPlaceholder')"
          />
        </ElFormItem>
      </ElForm>
      <template #footer>
        <ElButton
          @click="closeCreateProjectDialog"
          :disabled="projectsStore.isCreating"
        >
          {{ t("common.cancel") }}
        </ElButton>
        <ElButton
          type="primary"
          @click="handleCreateProject"
          :loading="projectsStore.isCreating"
          :disabled="!newProjectForm.name"
        >
          {{ t("common.create") }}
        </ElButton>
      </template>
    </ElDialog>

    <!--
      Заведение фичи из селектора шапки и перечень фич проекта —
      feature-lifecycle-R01, R11.
    -->
    <MbFeatureFormDialog
      v-model="showCreateFeatureDialog"
      :project-id="projectsStore.selectedProjectId"
    />

    <MbFeatureManagerDialog
      v-model="showFeatureManagerDialog"
      :project-id="projectsStore.selectedProjectId"
    />

    <!-- Commit Dialog -->
    <ElDialog
      v-model="showCommitDialog"
      :title="t('versioning.commitTitle')"
      width="500px"
      @close="closeCommitDialog"
    >
      <ElForm @submit.prevent="handleCommit">
        <!--
          Предел сообщения — 1000 символов независимо от алфавита
          (business-model-versioning-R05, основание: вердикт 231). Поле не
          позволяет ввести больше и показывает счётчик, поэтому отказ по
          длине из интерфейса недостижим; превышение иным путём отклоняет
          продукт.
        -->
        <ElFormItem :label="t('versioning.commitMessage')">
          <ElInput
            v-model="commitMessage"
            type="textarea"
            :rows="3"
            :maxlength="MAX_COMMIT_MESSAGE_LENGTH"
            show-word-limit
            :placeholder="t('versioning.commitMessagePlaceholder')"
          />
        </ElFormItem>
      </ElForm>
      <template #footer>
        <ElButton
          @click="closeCommitDialog"
          :disabled="versioningStore.isCommitting"
        >
          {{ t("common.cancel") }}
        </ElButton>
        <ElButton
          type="primary"
          @click="handleCommit"
          :loading="versioningStore.isCommitting"
        >
          {{ t("versioning.commit") }}
        </ElButton>
      </template>
    </ElDialog>

    <!-- History Dialog -->
    <ElDialog
      v-model="showHistoryDialog"
      :title="t('versioning.historyTitle')"
      width="600px"
      @close="closeHistoryDialog"
    >
      <div v-if="versioningStore.isLoadingHistory" class="history-loading">
        {{ t("common.loading") }}
      </div>
      <div v-else-if="versioningStore.commitCount === 0" class="history-empty">
        {{ t("versioning.noHistory") }}
      </div>
      <ul v-else class="history-list">
        <li
          v-for="commit in versioningStore.history?.commits ?? []"
          :key="commit.featureId"
          class="history-item"
          :class="{ 'history-item--head': commit.isHead }"
        >
          <div class="history-item__content">
            <div class="history-item__header">
              <span class="history-item__name">{{ commit.name }}</span>
              <ElTag v-if="commit.isHead" size="small" type="success">
                HEAD
              </ElTag>
            </div>
            <div class="history-item__message">
              {{ commit.message || t("versioning.noMessage") }}
            </div>
            <div class="history-item__meta">
              <span v-if="commit.committedAt">
                {{ new Date(commit.committedAt).toLocaleString() }}
              </span>
              <span v-if="commit.committedByName">
                {{ t("versioning.by") }} {{ commit.committedByName }}
              </span>
            </div>
          </div>
          <div class="history-item__actions">
            <ElButton
              v-if="!commit.isHead"
              size="small"
              @click="handleCheckout(String(commit.featureId))"
              :loading="versioningStore.isCheckingOut"
            >
              {{ t("versioning.checkout") }}
            </ElButton>
          </div>
        </li>
      </ul>
      <template #footer>
        <ElButton @click="closeHistoryDialog">
          {{ t("common.close") }}
        </ElButton>
      </template>
    </ElDialog>

    <!-- Checkout Dialog -->
    <ElDialog
      v-model="showCheckoutDialog"
      :title="t('versioning.checkoutTitle')"
      width="400px"
      @close="closeCheckoutDialog"
    >
      <ElForm @submit.prevent="confirmCheckout">
        <ElFormItem :label="t('versioning.branchName')">
          <ElInput
            v-model="checkoutBranchName"
            :placeholder="t('versioning.branchNamePlaceholder')"
            autofocus
          />
        </ElFormItem>
        <p class="checkout-hint">
          {{ t("versioning.branchNameHint") }}
        </p>
      </ElForm>
      <template #footer>
        <ElButton @click="closeCheckoutDialog">
          {{ t("common.cancel") }}
        </ElButton>
        <ElButton
          type="primary"
          :loading="versioningStore.isCheckingOut"
          @click="confirmCheckout"
        >
          {{ t("versioning.checkout") }}
        </ElButton>
      </template>
    </ElDialog>
  </ElContainer>
</template>

<style scoped>
.app-layout {
  height: 100vh;
  flex-direction: column;
}

/* Header */
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background-color: #fff;
  border-bottom: 1px solid var(--el-border-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  height: 60px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.app-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  margin-right: 8px;
}

.header-selector {
  width: 200px;
}

/* Content Container */
.app-content {
  height: calc(100vh - 60px);
}

/* Vertical Navigation Sidebar */
.nav-sidebar {
  width: 60px !important;
  background-color: #2c3e50;
  border-right: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.nav-items-top {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 8px;
}

.nav-items-bottom {
  padding: 12px 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  border-radius: 8px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s;
}

.nav-item:hover:not(.disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.nav-item.active {
  background-color: var(--el-color-primary);
  color: #fff;
}

.nav-item.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-item.user-button {
  color: #fff;
}

/* Admin navigation section */
.nav-admin-section {
  margin-bottom: 8px;
}

.nav-admin-divider {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.15);
  margin: 8px 4px;
}

/* User Menu */
.user-menu {
  padding: 8px 0;
}

.user-info {
  padding: 0 12px 8px;
}

.user-login {
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.user-name {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-top: 2px;
}

.user-menu-items {
  display: flex;
  flex-direction: column;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
}

.menu-item:hover {
  background-color: var(--el-fill-color-light);
}

.menu-item.danger {
  color: var(--el-color-danger);
}

.menu-item.danger:hover {
  background-color: var(--el-color-danger-light-9);
}

/* Main Content */
.app-main {
  padding: 0;
  background-color: #f5f7fa;
  overflow: hidden;
}

/* Selector styles */
.selector-divider {
  margin: 4px 0;
}

.option-text {
  margin-left: 8px;
}

.feature-status-tag {
  margin-left: 8px;
}

/* User menu styles */
.admin-tag {
  margin-top: 4px;
}

.user-menu-divider {
  margin: 12px 0;
}

/* History count tag */
.history-count-tag {
  margin-left: 4px;
}

/* History dialog */
.history-loading,
.history-empty {
  text-align: center;
  padding: 24px;
  color: var(--el-text-color-secondary);
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 400px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  transition: background-color 0.2s;
}

.history-item:hover {
  background-color: var(--el-fill-color-light);
}

.history-item:last-child {
  border-bottom: none;
}

.history-item--head {
  background-color: var(--el-color-success-light-9);
}

.history-item__content {
  flex: 1;
  min-width: 0;
}

.history-item__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.history-item__name {
  font-weight: 500;
  color: var(--el-text-color-primary);
}

.history-item__message {
  font-size: 13px;
  color: var(--el-text-color-regular);
  margin-bottom: 4px;
}

.history-item__meta {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  display: flex;
  gap: 12px;
}

.history-item__actions {
  flex-shrink: 0;
  margin-left: 12px;
}

/* Checkout dialog */
.checkout-hint {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin: 0;
}
</style>
